// The owner's other tabs: People (power users, cohorts, what a payer is worth), Restaurants (each
// place's own dashboard and its text list), Links & codes (make trackable links and promo codes), and
// Splits (every split, to mark test runs so they stay out of the numbers).
import { admin, type AdminCode } from '../api.ts';
import { brand } from '../../brand.ts';
import { app, logo, copy, esc, inputValue, ls, target, toast } from '../dom.ts';
import { creatorMaxCents, creatorTerms, perkText } from '../../offers.ts';
import type { UserRow } from '../../users.ts';
import type { VenueDetail } from '../../venues.ts';
import type { CalcLeadRow } from '../../leads.ts';
import { restaurantPitch } from '../../venue-pitch.ts';
import { CONTACT, fmtDollars, fmtTables } from '../../restaurant-calc.ts';
import type { SplitRow } from '../../test-splits.ts';
import type { ExperimentResult } from '../../experiment-stats.ts';
import { EXPERIMENT_SOURCES, FEE_GROUP_NAMES, type FeeGroup, type Rate } from '../../experiments.ts';
import { adminView, hbars, kpi, money, num, panel } from './admin.ts';

export type Tab = 'overview' | 'people' | 'restaurants' | 'codes' | 'experiments' | 'splits';
const TABS: Array<[Tab, string]> = [['overview', 'Overview'], ['people', 'People'], ['restaurants', 'Restaurants'], ['codes', 'Links & codes'], ['experiments', 'Experiments'], ['splits', 'Splits']];

export const tabsBar = (active: Tab) =>
  `<nav class="tabs" aria-label="Stats sections">${TABS.map(([t, l]) => `<button data-tab="${t}" aria-current="${t === active ? 'page' : 'false'}">${l}</button>`).join('')}</nav>`;

/** Tab switches and sign out, shared by every tab. True when the tap was one of those. */
export function handleShell(e: Event): boolean {
  const t = target<HTMLButtonElement>(e, '[data-tab]');
  if (t) { ls.set('adminTab', t.dataset.tab); venueOpen = null; adminView(); return true; }
  if (target(e, '[data-act=forget]')) { ls.set('adminToken', ''); adminView(); return true; }
  return false;
}

const shell = (tab: Tab, title: string, sub: string, inner: string) => `
  <div class="bar">${logo}<span>Stats</span></div>
  <div class="body dash">
    ${tabsBar(tab)}
    <div><h1 class="h">${esc(title)}</h1>${sub ? `<p class="sub" style="margin:4px 0 0">${sub}</p>` : ''}</div>
    ${inner}
    <div class="row2 narrow"><button class="btn2" data-tab="${tab}">Refresh</button><button class="btn2" data-act="forget">Sign out</button></div>
  </div>`;

/** A table whose cells are already-safe HTML. */
const htable = (head: string[], rows: string[][], empty = 'Nothing yet') => `
  <div class="tbl-wrap"><table class="tbl">
    <thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${rows.length ? rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('') : `<tr><td colspan="${head.length}" class="sub">${esc(empty)}</td></tr>`}</tbody>
  </table></div>`;

const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '–');

let venueOpen: string | null = null;
let peopleFilter: 'all' | 'power' | 'regular' | 'new' = 'all';

export function renderTab(tab: Tab, token: string): void {
  if (!app.querySelector('.dash')) app.innerHTML = `<div class="bar">${logo}<span>Stats</span></div><div class="body dash">${tabsBar(tab)}<p class="loading">Loading…</p></div>`;
  const fail = (err: Error) => {
    app.innerHTML = shell(tab, 'Something went wrong', '', `<div class="error">${esc(err.message)}</div>`);
    app.onclick = (e) => { handleShell(e); };
  };
  if (tab === 'people') admin.users(token).then((u) => people(u, token)).catch(fail);
  else if (tab === 'restaurants') {
    if (venueOpen) admin.venue(token, venueOpen).then((v) => venue(v)).catch(fail);
    else Promise.all([admin.venues(token), admin.leads(token)]).then(([v, l]) => venues(v.venues, l.leads, token, l.calcLeads || [])).catch(fail);
  } else if (tab === 'codes') admin.codes(token).then((c) => codes(c.codes, c.feeNetCents, token)).catch(fail);
  else if (tab === 'splits') admin.splits(token).then((r) => splits(r.splits, token)).catch(fail);
  else if (tab === 'experiments') admin.experiments(token, experimentSource).then((r) => experiments(r, token)).catch(fail);
}

// ---------- people ----------

const TIER: Record<UserRow['tier'], string> = { power: '<span class="pill good">Power</span>', regular: '<span class="pill watch">Regular</span>', new: '<span class="pill unknown">New</span>' };

function people(u: Awaited<ReturnType<typeof admin.users>>, token: string): void {
  const list = u.users.filter((x) => peopleFilter === 'all' || x.tier === peopleFilter);
  const v = u.value;
  app.innerHTML = shell('people', 'Payers', 'Everyone who has picked up a bill, best first. Only you see names and handles here.', `
    <div class="kpis">
      ${kpi('Payers', String(u.users.length), '', undefined, `Everyone who has picked up a bill with ${brand.name}, all time. One person across many dinners counts once, matched by their Venmo, Cash App or Zelle.`)}
      ${kpi('Power users', String(u.tiers.power), '3+ splits, or bring new payers', undefined, 'Payers with 3 or more splits, or 2 splits and at least one new payer brought in, or 12+ friends through their dinners. These are the people to thank, reward and ask for feedback.')}
      ${kpi('Regulars', String(u.tiers.regular), '2 splits', undefined, 'Payers who came back for a second split but aren\'t power users yet. One more dinner turns them into one.')}
      ${kpi('Splits per payer a year', num(v.splitsPerPayerPerYear), v.payersCounted ? `early estimate from ${v.payersCounted} payers` : 'needs payers 30+ days old', undefined, 'Each payer\'s splits so far, scaled up to a year, averaged over payers who started 30+ days ago. It\'s an early estimate and gets steadier as more payers age past their first month.')}
      ${kpi('Worth per payer a year', v.dollarsPerPayerPerYear === null ? '–' : `$${v.dollarsPerPayerPerYear}`, 'at $1 net per split', undefined, 'Splits per payer a year × $1, the planned net fee per split. It\'s what a payer would be worth if a fee is turned on. Today it\'s $0, because pricing is off.')}
      ${kpi('Friends per split', num(v.friendsPerSplit), 'people each dinner shows the app to', undefined, 'Friends who joined each split themselves, on average. It\'s the first half of the K-factor: more friends at the table means more chances to win a new payer.')}
    </div>
    ${panel('Who to look after', `
      <div class="chips" style="max-width:420px">${(['all', 'power', 'regular', 'new'] as const).map((f) => `<button class="chip" data-filter="${f}" aria-pressed="${f === peopleFilter}">${f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1)}</button>`).join('')}</div>
      ${htable(['Payer', 'Tier', 'Splits', 'Friends', 'New payers', 'Bills', 'Paid back', 'Last', 'Came from', 'Referral link'], list.slice(0, 100).map((x) => [
        `<b>${esc(x.name)}</b><div class="sub">${esc(x.handle)}</div>`,
        TIER[x.tier],
        String(x.splits), String(x.friendsBrought), x.newPayersBrought ? `<b>${x.newPayersBrought}</b>` : '0',
        esc(money(x.billCents)), esc(num(x.paidBackPct, '%')), esc(day(x.lastAt)),
        esc(x.source) + (x.refCode ? `<div class="sub">code ${esc(x.refCode)}</div>` : ''),
        x.referral
          ? `<b class="amt">${esc(x.referral.code)}</b> <span class="sub">${x.referral.uses} used</span>`
          : `<button class="ghost-btn" data-ref="${esc(x.key)}" data-name="${esc(x.name)}">Make link</button>`,
      ]), 'No payers in this group yet')}`,
      `"New payers" are friends from their dinners who later paid a bill with ${brand.name} themselves. That's the person to thank first.`)}
    <div class="grid2">
      ${panel('Payers coming back, by first month', htable(['Started', 'Payers', 'Back within 30 days', 'Back within 90 days', 'Splits each so far'], u.cohorts.map((c) => [
        esc(c.month), String(c.payers), esc(num(c.back30, '%')), esc(num(c.back90, '%')), String(c.splitsPerPayer),
      ])), 'A dash means that group hasn\'t been around long enough to tell yet.')}
      ${panel('Ways to reward power users', `<ul class="bn-f">
        <li><b>Referral link:</b> every payer gets a code on their first split, and friends' "Start a split" button carries it. "Make link" covers payers from before that. When friends use it, both get perks (say, a split with no fee once fees start). You see every use here.</li>
        <li><b>Founding member:</b> No fee, for good, for your first 100 power users. It costs little because they'd cover their fee with thanks anyway.</li>
        <li><b>Shout-out:</b> feature their dinner (with permission) in an Instagram post or video.</li>
        <li><b>Early access:</b> group trips, recurring dinners, or restaurant deals before anyone else.</li>
        <li><b>Ask them:</b> text your top 10 and ask what would make them use it every time. They'll tell you the next feature.</li>
      </ul>`)}
    </div>`);
  app.onclick = async (e) => {
    if (handleShell(e)) return;
    const f = target<HTMLButtonElement>(e, '[data-filter]');
    if (f) { peopleFilter = f.dataset.filter as typeof peopleFilter; return people(u, token); }
    const r = target<HTMLButtonElement>(e, '[data-ref]');
    if (r) {
      r.disabled = true;
      try {
        const name = r.dataset.name || '';
        const c = await admin.createCode(token, { kind: 'referral', ownerKey: r.dataset.ref, ownerName: name, label: `${name}'s referral link`, code: suggestCode(name) });
        await copy(c.link);
        toast(`${c.code} made. Link copied.`);
      } catch (err) { toast((err as Error).message); }
      renderTab('people', token);
    }
  };
}

/** JEN10 from "Jen", so codes are easy to say out loud. The server adds a number if it's taken. */
const suggestCode = (name: string) => (name.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8) || 'FRIEND') + '10';

// ---------- restaurants ----------

function venues(list: Awaited<ReturnType<typeof admin.venues>>['venues'], leads: Awaited<ReturnType<typeof admin.leads>>['leads'], token: string, calcLeads: CalcLeadRow[] = []): void {
  const optIns = list.reduce((a, v) => a + v.smsOptIns, 0);
  const people = list.reduce((a, v) => a + v.people, 0);
  const ready = list.filter((v) => v.splits >= 10);
  app.innerHTML = shell('restaurants', 'Restaurants', 'Every place a split happened, with its own dashboard. This is the pitch for restaurant texting and a white-label version.', `
    <div class="kpis">
      ${kpi('Restaurants', String(list.length))}
      ${kpi('Guests seen', String(people))}
      ${kpi('Said yes to texts', String(optIns), people ? `${Math.round((optIns / people) * 100)}% of guests` : '')}
      ${kpi('Ready to pitch', String(ready.length), '10+ splits there')}
    </div>
    ${panel('By restaurant', htable(['Restaurant', 'Splits', 'Guests', 'Avg party', 'Avg bill', 'Tip %', 'Groups back', 'Rating', 'Text list', 'Opt-in rate'], list.map((v) => [
      `<button class="link" data-venue="${esc(v.key)}" style="padding:0;font-weight:600;color:var(--ink);text-align:left">${esc(v.name)}</button><div class="sub">${esc(v.address)}${v.brandLocations > 1 ? ` · 1 of ${v.brandLocations} locations` : ''}</div>`,
      String(v.splits), String(v.people), esc(num(v.avgPartySize)), esc(money(v.avgBillCents)), esc(num(v.medianTipPct, '%')), String(v.groupsBack),
      v.survey.avgRating === null ? '–' : `${v.survey.avgRating} ★ <span class="sub">${v.survey.responses}</span>`,
      v.smsOptIns ? `<b>${v.smsOptIns}</b>` : '0',
      v.optInConversionPct === null ? '–' : `<b>${v.optInConversionPct}%</b> <span class="sub">of ${v.optInOffered}</span>`,
    ]), 'No splits yet'), 'Tap a restaurant to see its dashboard. Grouped by the name on the receipt.')}
    ${panel('Restaurants that wrote in', htable(['When', 'Restaurant', 'Locations', 'Table cards', 'Note'], leads.map((l) => [
      esc(day(l.at)),
      `<b>${esc(l.restaurant)}</b><div class="sub">${esc(l.name || '–')} · ${esc(l.contact)}</div>`,
      `<div style="white-space:normal">${l.locations.map((x) => `${esc(x.name)}<div class="sub">${esc([x.street, x.city].filter(Boolean).join(', ') || 'No address')}${x.tables ? ` · ${x.tables} tables` : ''}</div>`).join('')}</div>`,
      l.cards.length
        ? l.cards.map((c) => `<div><a href="/cards/${esc(c.code)}" target="_blank" rel="noopener">Print ${esc(c.code)}</a> <span class="sub">${esc(c.location)}</span></div>`).join('')
        : l.id ? `<button class="ghost-btn" data-cards="${esc(l.id)}">Make cards</button>` : '<span class="sub">–</span>',
      `<span style="white-space:normal">${esc(l.note)}</span>${l.from ? `<div class="sub">From ${esc(l.from)}</div>` : ''}`,
    ]), 'No one yet'), 'From the form on <a href="/restaurants" target="_blank" rel="noopener">/restaurants</a>. "Make cards" gives each location its own code; print its sheet and mail it. Splits that start from a card count under that code in Links &amp; codes.')}
    ${panel('Calculator leads', htable(['When', 'Restaurant tag', 'Email', 'Year'], calcLeads.map((l) => [
      esc(whenMt(l.at)),
      `<b>${esc(l.utm_content || '–')}</b><div class="sub">${esc([l.utm_source, l.utm_campaign].filter(Boolean).join(' · ') || 'No link tags')}</div>`,
      `<b>${esc(l.email)}</b><div class="sub">${esc([l.name, l.restaurant].filter(Boolean).join(' · ') || 'Wants a call')}${l.optIn ? ' · <b>wants news</b>' : ''}</div>`,
      `<b class="amt">${esc(fmtDollars(l.results?.year))}</b><div class="sub" style="white-space:normal;min-width:260px">${esc(calcLine(l))}</div>`,
    ]), 'No one yet'), `From "${esc(CONTACT)}" on the <a href="/restaurants#calculator" target="_blank" rel="noopener">/restaurants</a> calculator. Each one asked for a call: email them to set it up. Leads from before Oct 30 asked for their results instead. Only add someone to news if they asked for it.`)}`);
  app.onclick = (e) => {
    if (handleShell(e)) return;
    const b = target<HTMLButtonElement>(e, '[data-venue]');
    if (b) { venueOpen = b.dataset.venue || null; adminView(); }
    const mk = target<HTMLButtonElement>(e, '[data-cards]');
    if (mk) {
      mk.disabled = true;
      admin.makeCards(token, mk.dataset.cards || '')
        .then((r) => { toast(`Cards ready: ${r.cards.map((c) => c.code).join(', ')}`); renderTab('restaurants', token); })
        .catch((err: Error) => { toast(err.message); mk.disabled = false; });
    }
  };
}

/** Their numbers and the other results, for writing the email by hand. */
function calcLine(l: CalcLeadRow): string {
  const i = l.inputs, r = l.results;
  const n = (v: number | null | undefined) => (typeof v === 'number' ? String(v) : '–');
  const typed = `${n(i.tables)} tables split, ${n(i.saved)} min saved, ${n(i.turn)} min turn, $${n(i.check)} check, ${n(i.nights)} nights a week`;
  return r ? `${typed}. ${fmtTables(r.extraTables)} tables a night: ${fmtDollars(r.night)} night, ${fmtDollars(r.week)} week, ${fmtDollars(r.month)} month.` : `${typed}. No result (the numbers had a blank or an error).`;
}

/** Date and time in Mountain Time, so two sends from the same owner read apart. */
const whenMt = (iso: string) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' MT';

/** Weekly columns. */
function weeks(rows: VenueDetail['weeks']): string {
  const W = 600, H = 120, top = Math.max(1, ...rows.map((r) => r.splits)), bw = W / rows.length;
  return `<svg class="chart" viewBox="0 0 ${W} ${H + 18}" role="img" aria-label="Splits per week">
    <line x1="0" x2="${W}" y1="${H}" y2="${H}" stroke="var(--line)"/>
    ${rows.map((r, i) => {
      const h = (r.splits / top) * (H - 10);
      return `${h ? `<rect x="${(i * bw + bw * 0.15).toFixed(1)}" y="${(H - h).toFixed(1)}" width="${(bw * 0.7).toFixed(1)}" height="${h.toFixed(1)}" fill="var(--accent)" rx="2"><title>Week of ${esc(r.weekOf)}: ${r.splits} splits, ${r.people} guests</title></rect>` : ''}
        ${i % 3 === 0 || i === rows.length - 1 ? `<text x="${(i * bw + bw / 2).toFixed(1)}" y="${H + 14}" text-anchor="middle">${esc(r.weekOf.slice(5))}</text>` : ''}`;
    }).join('')}
  </svg>`;
}

function venue(v: VenueDetail): void {
  const pitch = restaurantPitch(v, money);
  app.innerHTML = shell('restaurants', v.name, `${esc(v.address)}. What this location would see on its own dashboard. Totals only. <button class="link" data-act="allVenues" style="padding:0">All restaurants</button>`, `
    <div class="kpis">
      ${kpi('Groups', String(v.splits))}
      ${kpi('Guests', String(v.people))}
      ${kpi('Avg party', num(v.avgPartySize))}
      ${kpi('Avg bill', money(v.avgBillCents))}
      ${kpi('Tip', num(v.medianTipPct, '%'), 'median, of the food')}
      ${kpi('Groups back', String(v.groupsBack), 'same payer, 2+ dinners')}
      ${kpi('Text list', String(v.smsOptIns), `${v.optInsLast30} in the last 30 days`)}
      ${kpi('Opt-in rate', num(v.optInConversionPct, '%'), `of ${v.optInOffered} guests who saw the offer`)}
      ${kpi('Guest rating', v.survey.avgRating === null ? '–' : `${v.survey.avgRating} ★`, `${v.survey.responses} answer${v.survey.responses === 1 ? '' : 's'}`)}
    </div>
    <div class="grid3">
      ${panel('How was it?', hbars(v.survey.ratings))}
      ${panel('The occasion', hbars(v.survey.occasions))}
      ${panel('What would bring them back sooner', hbars(v.survey.comeBack))}
    </div>
    <div class="grid2">
      ${panel('Groups per week', weeks(v.weeks))}
      ${panel('Party size', hbars(v.partySizes))}
    </div>
    <div class="grid2">
      ${panel('What people order', htable(['Item', 'Ordered', 'Avg price', 'Shared'], v.topItems.map((i) => [esc(i.name), String(i.times), esc(money(i.avgCents)), `${i.sharedPct}%`])),
        v.mostShared.length ? `Most shared: ${v.mostShared.map((m) => `${esc(m.name)} (${m.shared})`).join(', ')}.` : '')}
      ${panel('The pitch, ready to send', `<p style="margin:0">${esc(pitch)}</p><button class="btn2" data-act="copyPitch">Copy</button>`,
        `The restaurant gets the numbers of guests who opt in. The box starts unchecked. Reply STOP to opt out. We never sell phone numbers.`)}
    </div>
    ${panel('Recent groups', htable(['When', 'Guests', 'Bill', 'Tip', 'Said yes to texts'], v.recent.map((r) => [esc(day(r.createdAt)), String(r.people), esc(money(r.billCents)), esc(num(r.tipPct, '%')), String(r.optIns)])))}`);
  app.onclick = (e) => {
    if (handleShell(e)) return;
    if (target(e, '[data-act=allVenues]')) { venueOpen = null; adminView(); }
    if (target(e, '[data-act=copyPitch]')) void copy(pitch);
  };
}

// ---------- links & codes ----------

type Kind = 'campaign' | 'promo' | 'referral' | 'creator';

/** What each kind of link is for, and the words its form uses. Picking a kind comes first. */
const KINDS: Record<Kind, { chip: string; hint: string; who: string; whoHolder: string; codeHolder: string; perk: string; button: string }> = {
  campaign: {
    chip: 'Tracking', hint: 'Counts what one post, video or flyer brings in. No offer.',
    who: "What it's for", whoHolder: 'Instagram launch video', codeHolder: 'LAUNCH', perk: '', button: 'Make tracking link',
  },
  creator: {
    chip: 'Creator', hint: 'For a creator to post. Their followers get a perk, and the creator gets a stats page and a share of the fee from the new payers it brings.',
    who: "Creator's name", whoHolder: 'Maya Eats', codeHolder: 'MAYA', perk: 'What their followers get', button: 'Make creator link',
  },
  promo: {
    chip: 'Promo', hint: 'A code with a perk. Works as a link, or typed under "Have a code?".',
    who: "What it's for", whoHolder: 'Fall promo', codeHolder: 'FALL', perk: 'What they get', button: 'Make promo code',
  },
  referral: {
    chip: 'Referral', hint: "One person's link for their friends. Payers already get one on their first split, so this is for anyone else.",
    who: 'Whose link', whoHolder: 'Jen Park', codeHolder: 'JEN10', perk: 'What friends get', button: 'Make referral link',
  },
};
const ORDER: Kind[] = ['campaign', 'creator', 'promo', 'referral'];
const kindName = (c: AdminCode) => (c.kind === 'creator' ? `Creator · ${c.revSharePct ?? 50}%` : KINDS[c.kind].chip);

/** The choices on offer. A promo has to give something, so it has no "nothing". */
const PERKS = [0, 1, 3, 5];
const REWARD_SPLITS = [1, 3, 5];
const CAPS = [0, 50, 100, 500];

const select = (id: string, options: Array<[number, string]>, value: number) =>
  `<select id="${id}" class="input">${options.map(([v, l]) => `<option value="${v}"${v === value ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select>`;

let kind: Kind = 'campaign';
/** The form's picks, kept when switching kinds. */
const pick = { freeSplits: 1, revSharePct: 50, rewardSplits: 1, payerCap: 0, fixedFee: 0 };
/** A creator who already told followers a price: 0 lets the fee test decide, 1 to 3 lock group A, B or C. */
const FIXED_FEE = ['', 'A', 'B', 'C'] as const;
/** The link just made, shown in place of the form until "Make another". */
let made: { code: AdminCode; statsLink: string; message: string } | null = null;

/** A ready-to-send note for a creator: their link, their stats page and the deal, in plain words. */
export function creatorMessage(c: Pick<AdminCode, 'label' | 'link' | 'offer' | 'revSharePct' | 'rewardSplits' | 'payerCap'>, statsLink: string): string {
  const first = c.label.trim().split(/\s+/)[0] || 'there';
  return [
    `Hey ${first}! Here's your ${brand.name} link: ${c.link}`,
    ...(c.offer ? [`Your followers get: ${c.offer}. That's the line to put in your post.`] : []),
    `Anyone whose first split starts from your link counts as yours. You get ${creatorTerms(c)}. Fees aren't on yet, so we track it now and pay once they start.`,
    `Your stats, and a caption you can post, any time: ${statsLink}`,
  ].join('\n\n');
}

/** The deal and its worst case, under the creator fields. */
function dealLine(fee: number): string {
  const max = creatorMaxCents(pick, fee);
  return `They get ${esc(creatorTerms(pick))}. ${max === null ? 'No limit on what it can cost.' : `The most it can cost: <b>${esc(money(max))}</b> at ${esc(money(fee))} net a split.`}`;
}

const copyRow = (label: string, value: string) => `
  <div class="stack" style="gap:6px">
    <div class="lbl">${esc(label)}</div>
    <div class="amt" style="word-break:break-all;font-size:14px">${esc(value)}</div>
    <button type="button" class="btn2" data-copy="${esc(value)}">Copy</button>
  </div>`;

function madeCard(m: NonNullable<typeof made>): string {
  const c = m.code;
  return `<div class="stack made" style="gap:14px">
    <p style="margin:0"><b class="amt">${esc(c.code)}</b> is ready. ${c.kind === 'creator' ? 'Send them the message below.' : 'The link is copied.'}</p>
    ${copyRow(c.kind === 'creator' ? 'Their link' : 'Link', c.link)}
    ${c.kind === 'creator' ? copyRow('Their stats page (only send it to them)', m.statsLink) : ''}
    ${c.kind === 'creator' ? `<div class="stack" style="gap:6px"><div class="lbl">Message to send them</div>
      <p class="sub" style="margin:0;white-space:pre-wrap;overflow-wrap:anywhere">${esc(m.message)}</p>
      <button type="button" class="btn" data-copy="${esc(m.message)}">Copy message</button></div>` : ''}
    <button type="button" class="btn2" data-act="another">Make another</button>
  </div>`;
}

/** Where a link gets posted: the utm_source it carries, and the utm_medium that goes with it. */
export const SOURCES: Array<[string, string, string]> = [
  ['instagram', 'Instagram', 'social'], ['tiktok', 'TikTok', 'social'], ['facebook', 'Facebook', 'social'],
  ['x', 'X (Twitter)', 'social'], ['youtube', 'YouTube', 'social'], ['email', 'Email', 'email'],
  ['sms', 'Text message', 'sms'], ['promo-card', 'Promo card', 'print'], ['flyer', 'Flyer or poster', 'print'], ['other', 'Somewhere else', ''],
];
/** Tracking and promo links are about where they're posted, so those ask up front; the others fill it in. */
const tagsUp = () => kind === 'campaign' || kind === 'promo';

function tagFields(): string {
  const fields = `
    <div class="row2">
      <div class="field"><label for="cSource">Where it's posted</label>
        <select id="cSource" class="input">${tagsUp() ? '<option value="">Pick one</option>' : '<option value="">Fill in for me</option>'}${SOURCES.map(([v, l]) => `<option value="${v}">${esc(l)}</option>`).join('')}</select></div>
      <div class="field"><label for="cCampaign">Campaign${tagsUp() ? ' <span class="sub">(optional)</span>' : ''}</label><input id="cCampaign" class="input" placeholder="fall-launch" autocapitalize="none"></div>
    </div>
    <div class="field source-other" hidden><label for="cSourceOther">Where, exactly</label><input id="cSourceOther" class="input" placeholder="podcast" autocapitalize="none"></div>
    <p class="sub" style="margin:0">Saved on the link as utm_source, utm_medium and utm_campaign. Campaign groups links, like every post for one launch. Left blank, it's the code.</p>`;
  return tagsUp() ? fields : `<details class="tags"><summary class="sub">Where it's posted <span class="sub">(optional, filled in for you)</span></summary><div class="stack" style="gap:12px;margin-top:10px">${fields}</div></details>`;
}

/** The UTM tags from the form: the source picked (or typed), and the medium that goes with it. */
function readTags(): { source: string; medium: string; campaign: string } {
  const picked = inputValue('cSource');
  const source = picked === 'other' ? inputValue('cSourceOther') : picked;
  return { source, medium: SOURCES.find(([v]) => v === picked)?.[2] || '', campaign: inputValue('cCampaign') };
}

/** "Instagram · social", with the campaign under it. */
function tagCell(c: AdminCode): string {
  const name = SOURCES.find(([v]) => v === c.utm.source)?.[1] || c.utm.source || '–';
  return `${esc(name)}${c.utm.medium ? ` <span class="sub">· ${esc(c.utm.medium)}</span>` : ''}${c.utm.campaign ? `<div class="sub">${esc(c.utm.campaign)}</div>` : ''}`;
}

/** Every link's numbers added up by where it was posted, best first. */
function bySource(list: AdminCode[]): string[][] {
  const rows = new Map<string, { links: number; visits: number; newPayers: number; splits: number; joins: number }>();
  for (const c of list) {
    const r = rows.get(c.utm.source) || { links: 0, visits: 0, newPayers: 0, splits: 0, joins: 0 };
    r.links++; r.visits += c.visits; r.newPayers += c.newPayers; r.splits += c.splits; r.joins += c.joins;
    rows.set(c.utm.source, r);
  }
  return [...rows].sort((a, b) => b[1].newPayers - a[1].newPayers || b[1].splits - a[1].splits || b[1].visits - a[1].visits).map(([src, r]) => [
    esc(SOURCES.find(([v]) => v === src)?.[1] || src || '–'), String(r.links), String(r.visits), `<b>${r.newPayers}</b>`, String(r.splits), String(r.joins),
    r.visits ? `${Math.round((r.splits / r.visits) * 100)}%` : '–',
  ]);
}

function codeForm(fee: number): string {
  const k = KINDS[kind];
  const perks = PERKS.filter((n) => n || kind !== 'promo');
  const perk = kind === 'promo' && !pick.freeSplits ? 1 : pick.freeSplits;
  return `<form id="codeForm" class="stack" style="gap:12px" autocomplete="off">
    <div class="field"><label>What kind of link?</label>
      <div class="chips">${ORDER.map((x) => `<button type="button" class="chip" data-kind="${x}" aria-pressed="${x === kind}">${KINDS[x].chip}</button>`).join('')}</div>
      <p class="sub kind-hint" style="margin:0">${esc(k.hint)}</p>
    </div>
    <div class="field"><label for="cWho" id="cWhoLabel">${esc(k.who)}</label><input id="cWho" class="input" placeholder="${esc(k.whoHolder)}"></div>
    <div class="field"><label for="cCode">Code <span class="sub">(optional)</span></label><input id="cCode" class="input amt" placeholder="${esc(k.codeHolder)}" autocapitalize="characters" maxlength="20"></div>
    ${k.perk ? `<div class="field"><label for="cPerk">${esc(k.perk)}</label>
      ${select('cPerk', perks.map((n) => [n, perkText(n) || 'Nothing extra']), perk)}
      <p class="sub" style="margin:0">Their first splits after they start from this link. Recorded now, honored once fees start.</p></div>` : ''}
    ${kind === 'creator' ? `
    <div class="row2">
      <div class="field"><label for="cShare">Their share of the fee</label>${select('cShare', [25, 50, 75, 100].map((n) => [n, `${n}%`]), pick.revSharePct)}</div>
      <div class="field"><label for="cReward">Paid splits per payer</label>${select('cReward', REWARD_SPLITS.map((n) => [n, n === 1 ? 'Just the first' : `First ${n}`]), pick.rewardSplits)}</div>
    </div>
    <div class="field"><label for="cCap">For</label>${select('cCap', CAPS.map((n) => [n, n ? `Their first ${n} new payers` : 'Every new payer they bring']), pick.payerCap)}</div>
    <div class="field"><label for="cFixed">Price their followers see</label>${select('cFixed', [[0, 'Whatever the fee test picks'], [1, `Locked: ${FEE_GROUP_NAMES.A}`], [2, `Locked: ${FEE_GROUP_NAMES.B}`], [3, `Locked: ${FEE_GROUP_NAMES.C}`]], pick.fixedFee)}
      <p class="sub" style="margin:0">Lock it only if they already quoted a price. Locked splits stay out of the test's results.</p></div>
    <p class="note deal" style="margin:0">${dealLine(fee)}</p>` : ''}
    ${tagFields()}
    <button class="btn" data-act="makeCode">${esc(k.button)}</button>
  </form>`;
}

/** Read the dropdowns into `pick`. */
function readPicks(): void {
  const n = (id: string) => (document.getElementById(id) as HTMLSelectElement | null)?.value;
  for (const [id, key] of [['cPerk', 'freeSplits'], ['cShare', 'revSharePct'], ['cReward', 'rewardSplits'], ['cCap', 'payerCap'], ['cFixed', 'fixedFee']] as const) {
    const v = n(id);
    if (v !== undefined) pick[key] = Number(v);
  }
}

/** Switch the form to another kind, keeping what's been typed and picked. */
function setKind(next: Kind, fee: number): void {
  const keep = ['cWho', 'cCode', 'cSource', 'cSourceOther', 'cCampaign'].map((id) => [id, inputValue(id)] as const);
  const open = (document.querySelector('details.tags') as HTMLDetailsElement | null)?.open;
  readPicks();
  kind = next;
  const form = document.getElementById('codeForm');
  if (!form) return;
  form.outerHTML = codeForm(fee);
  for (const [id, v] of keep) if (v) (document.getElementById(id) as HTMLInputElement).value = v;
  if (open) (document.querySelector('details.tags') as HTMLDetailsElement | null)?.setAttribute('open', '');
  showOther();
}

const showOther = () => { const o = document.querySelector('.source-other') as HTMLElement | null; if (o) o.hidden = inputValue('cSource') !== 'other'; };

function codes(list: AdminCode[], fee: number, token: string): void {
  const auto = list.filter((c) => c.auto).length;
  app.innerHTML = shell('codes', 'Links & codes', 'Make a link for every post, video or partner, and see what each one brings in.', `
    <div class="grid2">
      ${panel(made ? 'Your new link' : 'Make a link', made ? madeCard(made) : codeForm(fee), made ? '' : 'Leave the code blank to get one made from the name.')}
      ${panel('How it works', `<ul class="bn-f">
        <li>The link opens ${brand.name} with <b>?ref=CODE</b>. The phone remembers the code.</li>
        <li>When that phone starts a split, the code is saved on it, and the perk shows as "applied" on the setup screen.</li>
        <li>People can also type a code under "Have a code?" when they set up a split.</li>
        <li>A payer's first split decides who brought them. Their splits with no fee count from there.</li>
        <li><b>Creators</b> earn their share on the paid splits after the ones with no fee, up to the number you pick, for as many payers as you pick. It's all tracked now and estimated until fees start.</li>
      </ul>`)}
    </div>
    ${list.some((c) => !c.auto) ? panel('By where it was posted', htable(['Posted on', 'Links', 'Visits', 'New payers', 'Splits', 'Friends joined', 'Visit to split'], bySource(list.filter((c) => !c.auto)))) : ''}
    ${panel('Your links', htable(['Code', 'For', 'Type', 'Offer', 'Posted on', 'Visits', 'New payers', 'Splits', 'Friends joined', 'Creator earned', 'Link', ''], list.filter((c) => !c.auto).map((c) => [
      `<b class="amt">${esc(c.code)}</b>`, esc(c.label) + (c.ownerName ? `<div class="sub">${esc(c.ownerName)}</div>` : ''),
      esc(kindName(c)) + (c.kind === 'creator' ? `<div class="sub">${esc(creatorTerms(c).replace(/^\d+% of the fee on the /, ''))}</div>` : ''), esc(c.offer || '–'), tagCell(c),
      String(c.visits), `<b>${c.newPayers}</b>` + (c.overCap ? `<div class="sub">${c.overCap} past the limit</div>` : ''), String(c.splits), String(c.joins),
      c.kind === 'creator' ? `${esc(money(c.earnedCents))}<div class="sub">estimated</div>` : '–',
      `<button class="ghost-btn" data-copy="${esc(c.link)}">Copy link</button>${c.partnerToken ? ` <button class="ghost-btn" data-copy="${esc(creatorMessage(c, statsLink(c)))}">Copy message</button>` : ''}`,
      `<button class="link" data-toggle="${esc(c.code)}" data-on="${c.active ? '0' : '1'}">${c.active ? 'Turn off' : 'Turn on'}</button>`,
    ]), 'No links yet. Make one above.'), auto ? `Plus ${auto} referral codes made for payers on their first split. They're in People, next to each payer.` : '')}`);
  app.onsubmit = (e) => e.preventDefault();
  app.onchange = () => {
    showOther();
    readPicks();
    const deal = document.querySelector('.deal');
    if (deal) deal.innerHTML = dealLine(fee);
  };
  app.onclick = async (e) => {
    if (handleShell(e)) return;
    const k = target<HTMLButtonElement>(e, '[data-kind]');
    if (k) return setKind(k.dataset.kind as Kind, fee);
    const c = target<HTMLButtonElement>(e, '[data-copy]');
    if (c) return void copy(c.dataset.copy || '');
    if (target(e, '[data-act=another]')) { made = null; return codes(list, fee, token); }
    const t = target<HTMLButtonElement>(e, '[data-toggle]');
    if (t) {
      await admin.setCodeActive(token, t.dataset.toggle || '', t.dataset.on === '1').catch((err: Error) => toast(err.message));
      return renderTab('codes', token);
    }
    const m = target<HTMLButtonElement>(e, '[data-act=makeCode]');
    if (m) {
      const who = inputValue('cWho');
      if (!who) return toast(kind === 'creator' ? "Add the creator's name." : kind === 'referral' ? 'Add whose link it is.' : "Say what it's for.");
      if (tagsUp() && !readTags().source) return toast(inputValue('cSource') === 'other' ? 'Say where it\'s posted.' : 'Pick where it\'s posted.');
      readPicks();
      m.disabled = true;
      try {
        const code = await admin.createCode(token, {
          kind, code: inputValue('cCode'),
          label: kind === 'referral' ? `${who}'s referral link` : who,
          freeSplits: KINDS[kind].perk ? pick.freeSplits : 0,
          ...(kind === 'referral' ? { ownerName: who } : {}),
          ...(kind === 'creator' ? { revSharePct: pick.revSharePct, rewardSplits: pick.rewardSplits, payerCap: pick.payerCap, ...(FIXED_FEE[pick.fixedFee] ? { fixedFee: FIXED_FEE[pick.fixedFee] as FeeGroup } : {}) } : {}),
          utm: readTags(),
        });
        const stats = statsLink(code);
        made = { code, statsLink: stats, message: code.kind === 'creator' ? creatorMessage(code, stats) : '' };
        if (code.kind !== 'creator') await copy(code.link);
        renderTab('codes', token);
      } catch (err) {
        toast((err as Error).message);
        m.disabled = false;
      }
    }
  };
}

const statsLink = (c: Pick<AdminCode, 'partnerToken'>) => (c.partnerToken ? `${location.origin}/partner/${c.partnerToken}` : '');

// ---------- splits ----------

const when = (iso: string) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '–');

function splits(list: SplitRow[], token: string): void {
  const tests = list.filter((s) => s.test).length;
  app.innerHTML = shell('splits', 'Splits', 'Every split, newest first. Mark your test runs as tests and they drop out of every number on these pages. They still work, and nothing is deleted, so you can undo it.', `
    <div class="kpis">
      ${kpi('Splits', String(list.length))}
      ${kpi('Counted', String(list.length - tests))}
      ${kpi('Marked as tests', String(tests))}
    </div>
    ${panel('All splits', htable(['When', 'Code', 'Restaurant', 'Payer', 'People', 'Paid back', 'Bill', 'Status', ''], list.map((s) => [
      esc(when(s.createdAt)),
      `<a class="amt" href="/s/${esc(s.code)}" target="_blank" rel="noopener">${esc(s.code)}</a>`,
      esc(s.venue || '–'),
      `<b>${esc(s.payer || '–')}</b>${s.handle ? `<div class="sub">${esc(s.handle)}</div>` : ''}`,
      String(s.people),
      s.friends ? `${s.friendsPaid} of ${s.friends}` : '–',
      esc(money(s.totalCents)),
      s.test ? '<span class="pill unknown">Test</span>' : esc(s.status === 'closed' ? 'Closed' : 'Open'),
      `<button class="ghost-btn" data-test="${esc(s.code)}" data-on="${s.test ? '0' : '1'}">${s.test ? 'Count it' : 'Mark as test'}</button>`,
    ]), 'No splits yet'), 'Test splits are left out of Overview, People, Restaurants and Links & codes, along with everything that happened on them, and visits from a phone whose only splits were tests.')}`);
  app.onclick = async (e) => {
    if (handleShell(e)) return;
    const b = target<HTMLButtonElement>(e, '[data-test]');
    if (!b) return;
    b.disabled = true;
    try {
      const r = await admin.setTest(token, b.dataset.test || '', b.dataset.on === '1');
      toast(r.test ? `${r.code} marked as a test.` : `${r.code} counts again.`);
    } catch (err) { toast((err as Error).message); }
    renderTab('splits', token);
  };
}

// ---------- experiments ----------

let experimentSource = '';
const SOURCE_NAMES: Record<string, string> = { '': 'All', creator: 'Creators', referral: 'Referrals', ads: 'Ads', table: 'Tables', sms: 'Our texts', direct: 'Direct' };

const rate = (r: Rate | null) => (r === null ? '<span class="sub">Not live yet</span>' : r.den ? `<b>${Math.round((r.num / r.den) * 100)}%</b><div class="sub">${num(r.num)} of ${num(r.den)}</div>` : '<span class="sub">–</span>');
const secs = (n: number | null) => (n === null ? '–' : n < 3600 ? `${Math.max(1, Math.round(n / 60))} min` : n < 86_400 ? `${Math.round(n / 3600)} h` : `${Math.round(n / 86_400)} days`);

function experiments(r: ExperimentResult, token: string): void {
  const groups = r.rows;
  const head = ['', ...groups.map((g) => `${g.group}. ${g.name}`)];
  const row = (label: string, cells: string[]) => [`<b>${esc(label)}</b>`, ...cells];
  const status = !r.on
    ? 'The fee test is off. Set FEE_TEST=on in Railway Variables to start it (only once card charges are live).'
    : r.callable
      ? `Every group has at least ${num(r.minPayers)} payers. Ahead on net revenue per payer: <b>${esc(r.leader ? `${r.leader}. ${FEE_GROUP_NAMES[r.leader]}` : '–')}</b>. Check the guardrail rows before picking it.`
      : `Too early to call. Each group needs ${num(r.minPayers)} payers; differences before then are hints, not proof.`;
  app.innerHTML = shell('experiments', 'Experiments', `The fee test: ${money(r.cents)} on the payer's card in every group. What changes is what friends add. Each payer stays in the group of their first split; tables of 2 and creator codes with a fixed price are left out.`, `
    <div class="note">${status}</div>
    <div class="chips" role="radiogroup" aria-label="Where payers came from">${['', ...EXPERIMENT_SOURCES].map((k) => `<button class="chip" role="radio" data-src="${k}" aria-pressed="${k === r.source}">${esc(SOURCE_NAMES[k])}</button>`).join('')}</div>
    ${panel('Fee test, side by side', htable(head, [
      row('Payers', groups.map((g) => `<b>${num(g.payers)}</b>${g.payers < r.minPayers ? `<div class="sub">${num(r.minPayers - g.payers)} more to call it</div>` : ''}`)),
      row('Splits', groups.map((g) => num(g.splits))),
      row('Net revenue per payer, first 60 days', groups.map((g) => `<b>${money(g.netPerPayerCents)}</b><div class="sub">${g.netEstimated ? 'Estimated: fee less Stripe, on splits a friend joined' : 'From card charges'}</div>`)),
      row('Split again within 60 days', groups.map((g) => rate(g.secondSplit))),
      row('Card saved, when asked', groups.map((g) => rate(g.cardSaved))),
      row('Landing page to first split', groups.map((g) => rate(g.landingToSplit))),
      row('Friends who paid', groups.map((g) => rate(g.friendsPaid))),
      row('Median time to paid', groups.map((g) => secs(g.medianSecsToPaid))),
      row('Friends added on top, each', groups.map((g) => money(g.extraPerFriendCents))),
      row('Friends who started their own split', groups.map((g) => rate(g.friendsBecamePayers))),
    ], 'No groups yet'), 'The decision is net revenue per payer. The last row is the guardrail: a group that earns more but brings fewer new payers to the table loop does not win.')}
    ${r.fixedSplits ? `<p class="sub">${num(r.fixedSplits)} splits came from creator codes with a fixed price and are left out.</p>` : ''}`);
  app.onclick = (e) => {
    if (handleShell(e)) return;
    const b = target<HTMLButtonElement>(e, '[data-src]');
    if (!b) return;
    experimentSource = b.dataset.src || '';
    renderTab('experiments', token);
  };
}
